import React from 'react';

import { Button } from '@capintel/component-lib/button';
import ActionsButton, { buttonAction } from '@/views/components/buttons/ActionsButton';
import ConnectedPrivateComponent from '@/views/components/PrivateComponent';
import { SearchInput } from '@/views/components/inputs';

import useScreenSize from '@/hooks/useScreenSize';

import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { truncateString } from '@/utils/objects';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import { canAccessUploadCSV } from '@/utils/feature-access';

interface ContactPortfoliosHeaderProps {
  contact?: GenericContact;
  portfolios?: Portfolio[];
  searching: boolean;
  query: string;
  hasPersistenceKeySource: boolean;
  evaluatingPortfolioForComparison: boolean;
  user?: User | null;
  isActiveDataSourceTabPostMessage: boolean;
  onSearch: (query: string) => void;
  onCreatePortfolio: () => void;
  showPortfolioUploadModal: () => void;
}

export const ContactPortfoliosHeader: React.FC<ContactPortfoliosHeaderProps> = ({
  contact,
  portfolios,
  searching,
  query,
  hasPersistenceKeySource,
  evaluatingPortfolioForComparison,
  user,
  isActiveDataSourceTabPostMessage,
  onSearch,
  onCreatePortfolio,
  showPortfolioUploadModal,
}) => {
  const { isMobile } = useScreenSize();
  const isUploadCSVFeatureEnabled = canAccessUploadCSV(user);

  const uploadCsvFileProps: buttonAction = {
    'data-testid': 'contactPortfolios-button-createUploadCsvFile',
    condition: isUploadCSVFeatureEnabled,
    icon: 'file-upload',
    node: <FormattedMessage id="Contacts.ContactTabs.PortfoliosV2.UploadCSVFile" />,
    onClick: showPortfolioUploadModal,
  };

  const getActionsForCreatePortfolioButton = (): buttonAction[] => {
    if (isActiveDataSourceTabPostMessage) return [];

    return [
      {
        'data-testid': 'contactPortfolios-button-createClientPortfolio',
        condition: userHasPermission(user, PERMISSIONS.PORTFOLIOS_CREATE),
        icon: 'add',
        node: <FormattedMessage id="Contacts.ContactTabs.PortfoliosV2.CreatePortfolio" />,
        onClick: onCreatePortfolio,
      },
      {
        ...uploadCsvFileProps,
        condition: !isMobile && isUploadCSVFeatureEnabled,
      },
    ];
  };

  const showActions = portfolios?.length || query || searching;

  return (
    <div className="contact-portfolios-header">
      <div className="contact-portfolios-title">
        <FormattedMessage
          data-private
          data-dd-privacy="mask"
          id="Contacts.ContactTabs.Portfolios.Header"
          values={{ name: truncateString(contact?.fname || '', 80) }}
        />
      </div>
      {showActions && (
        <div className="contact-portfolios-header-actions">
          <ConnectedPrivateComponent permissions={[PERMISSIONS.PORTFOLIOS_FILTER]}>
            {() => (
              <SearchInput
                className="search-input-v2"
                data-testid="contactPortfolios-searchInput-searchPortfolios"
                loading={searching}
                placeholder={formatMessage('Contacts.ContactTabs.PortfoliosV2.SearchPlaceholder')}
                value={query}
                onChange={onSearch}
              />
            )}
          </ConnectedPrivateComponent>
          <ConnectedPrivateComponent forcedFalse={hasPersistenceKeySource} permissions={[PERMISSIONS.PORTFOLIOS_CREATE]}>
            {() => <ActionsButton actions={getActionsForCreatePortfolioButton()} disabled={evaluatingPortfolioForComparison} />}
          </ConnectedPrivateComponent>
          {isMobile && (
            <Button
              variant="secondary"
              onClick={uploadCsvFileProps.onClick}
              data-testid={uploadCsvFileProps['data-testid']}
              disabled={!uploadCsvFileProps.condition}
              icon="file-upload"
            >
              {uploadCsvFileProps.node}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
